import { Capacitor } from '@capacitor/core';
import { Keyboard } from '@capacitor/keyboard';

/**
 * Suppression of the iOS keyboard input-accessory bar (the AutoFill / arrows /
 * Done row Safari draws above the keyboard).
 *
 * Native wrapper (Capacitor) ONLY: the Keyboard plugin hides the bar for the
 * whole session with one call. This is the only path that reliably works.
 *
 * There is no web path anymore. We shipped the "inputmode toggle" hack for
 * mobile Safari — both the focus-time variant and the earlier-arming
 * pointerdown variant — and confirmed on real devices (iPhone SE and current
 * iPhones, verified via appetizer.io) that neither suppresses the bar on
 * today's iOS/WebKit: WebKit finalizes the keyboard's configuration (accessory
 * bar included) at focus-commit and no longer drops the bar when inputmode is
 * flipped none→text around it. The hack also cost a ~50ms keyboard-open delay
 * on every field for nothing, so it was removed. See the WebKit note below if
 * you want to try again on a future iOS.
 *
 * The footer dock never assumed the hack worked: it measures the actual
 * keyboard region from the VisualViewport (see useKeyboardPin), so the dock
 * seats itself correctly whether or not the bar is present. That stays true —
 * on web the bar simply shows, and the dock accounts for its height live.
 *
 * ── Future web-side ideas to try (all unverified; the accessory bar has no
 *    sanctioned web API, so anything here is a WebKit-version-specific hack) ──
 *
 *  1. `inputmode="none"` + a custom in-DOM keyboard/picker for the field. This
 *     genuinely removes the bar because it removes the system keyboard entirely
 *     — the right answer for PIN pads / pickers, but not for free-text fields
 *     like the share-wizard summary where users want the real keyboard.
 *  2. contenteditable div instead of <input>/<textarea>. WebKit has historically
 *     drawn no accessory bar for contenteditable regions. Heavy tradeoff: you
 *     give up native input semantics (value, maxlength, selection, IME quirks,
 *     Ionic's IonInput/IonTextarea integration) and must re-implement them.
 *  3. Re-test the pointerdown-arming inputmode toggle on each major iOS release —
 *     the behavior has changed across point releases before, so a future WebKit
 *     could reopen the none-at-focus-commit loophole. The removed implementation
 *     is in git history (search the PR that added this file) if it's worth
 *     resurrecting.
 *  4. Watch for a standardized API: the VirtualKeyboard API
 *     (`navigator.virtualKeyboard`) is Chromium-only today, but if WebKit ever
 *     ships it — or Apple adds a first-party knob — prefer that over any hack.
 *
 * Anything reintroduced here must stay best-effort: keep the live-measurement
 * contract in useKeyboardPin intact and never bake in an assumed bar height.
 */

/**
 * Suppress the keyboard input-accessory bar for the whole app. Call once early
 * in the application lifecycle (a root App effect). Returns an uninstaller for
 * symmetry with effect cleanup; today it is always a no-op (the native plugin
 * call has nothing to tear down, and there is no web listener to remove).
 *
 * Best-effort by design — see the module comment: on web the bar shows and
 * consumers keep working (the dock measures the live keyboard region).
 */
export function suppressAccessoryBar(): () => void {
  // Native wrapper: the Keyboard plugin hides the bar outright. On web there is
  // nothing to do — no working suppression exists (see the module comment).
  if (Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('Keyboard')) {
    Keyboard.setAccessoryBarVisible({ isVisible: false }).catch(error => {
      // eslint-disable-next-line no-console
      console.error('Failed to hide the keyboard accessory bar:', error);
    });
  }
  return () => {};
}
